import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  Check,
  ChevronDown,
  CircleHelp,
  LifeBuoy,
  Mail,
  MessageSquareText,
} from "lucide-react"
import { useState } from "react"

import { WorkspacePageHeader } from "@/components/workspace-page-header"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useDemoResetMaintenance } from "@/lib/demo-reset-status"
import { helpQueryOptions } from "@/lib/query-options"
import { submitFeedbackFn } from "@/server/functions"

export const Route = createFileRoute("/portal/help")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(helpQueryOptions()),
  head: () => ({
    meta: [{ title: "Help and feedback · Service Portal demo" }],
  }),
  component: HelpPage,
})

function HelpPage() {
  const resetInProgress = useDemoResetMaintenance()
  const { data } = useSuspenseQuery(helpQueryOptions())
  const [observation, setObservation] = useState("")
  const [suggestion, setSuggestion] = useState("")
  const feedbackMutation = useMutation({
    mutationFn: () => submitFeedbackFn({ data: { observation, suggestion } }),
  })

  return (
    <div className="mx-auto max-w-[1300px]">
      <WorkspacePageHeader
        kicker="Service guidance"
        title="Help and feedback"
        description="Review the imagery-request workflow or send a synthetic support note to demonstrate the production feedback path."
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <section className="overflow-hidden rounded-[14px] border border-border bg-white">
          <div className="border-b border-border px-5 py-4 sm:px-6">
            <h2 className="flex items-center gap-2 font-semibold text-[var(--navy)]">
              <CircleHelp className="size-4 text-[var(--service-blue)]" />
              Service guide
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Answers derived from the referenced workflow
            </p>
          </div>
          <div className="divide-y divide-border">
            {data.topics.map((topic, index) => (
              <details
                key={topic.id}
                className="group px-5 py-4 open:bg-[#fbfcfc] sm:px-6"
                open={index === 0}
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-[8px] font-semibold text-[var(--navy)] focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-[var(--cyan)]">
                  <span>
                    <span className="mb-1 block text-[0.62rem] font-semibold text-[var(--service-blue)]">
                      {topic.category}
                    </span>
                    {topic.question}
                  </span>
                  <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 max-w-[75ch] text-sm leading-7 text-muted-foreground">
                  {topic.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        <aside className="space-y-6">
          <section className="rounded-[14px] bg-[var(--navy)] p-5 text-white sm:p-6">
            <span className="grid size-11 place-items-center rounded-[10px] bg-white/8 text-[var(--cyan)]">
              <LifeBuoy className="size-5" />
            </span>
            <h2 className="mt-5 text-xl font-semibold tracking-[-0.02em]">
              Demo support desk
            </h2>
            <p className="mt-2 text-sm leading-6 text-white/64">
              Contact information below is intentionally non-production.
            </p>
            <div className="mt-5 grid gap-3 border-t border-white/12 pt-4 text-xs">
              <p className="flex items-center gap-2 text-white/76">
                <Mail className="size-4 text-[var(--cyan)]" />
                {data.contact}
              </p>
              <p className="text-white/60">{data.serviceHours}</p>
            </div>
          </section>

          <section className="rounded-[14px] border border-border bg-white p-5 sm:p-6">
            {feedbackMutation.data ? (
              <div>
                <span className="grid size-11 place-items-center rounded-[10px] bg-[#eaf6ef] text-[#246947]">
                  <Check className="size-5" />
                </span>
                <h2 className="mt-5 text-xl font-semibold text-[var(--navy)]">
                  Feedback recorded
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  The note exists only for this interaction and is not sent to a
                  production support system.
                </p>
                <p className="mt-4 font-mono text-xs font-semibold text-[var(--service-blue)]">
                  {feedbackMutation.data.reference}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-5"
                  onClick={() => {
                    setObservation("")
                    setSuggestion("")
                    feedbackMutation.reset()
                  }}
                >
                  Send another demo note
                </Button>
              </div>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  feedbackMutation.mutate()
                }}
              >
                <h2 className="flex items-center gap-2 font-semibold text-[var(--navy)]">
                  <MessageSquareText className="size-4 text-[var(--service-blue)]" />
                  Your feedback
                </h2>
                <div className="mt-5 grid gap-2">
                  <Label htmlFor="observation">What did you encounter?</Label>
                  <Textarea
                    id="observation"
                    value={observation}
                    onChange={(event) => setObservation(event.target.value)}
                    placeholder="Describe the page, action, and result."
                    minLength={12}
                    maxLength={1200}
                    rows={5}
                    required
                    className="rounded-[10px] border-border bg-white"
                  />
                </div>
                <div className="mt-4 grid gap-2">
                  <Label htmlFor="suggestion">
                    Other comments or suggestions
                  </Label>
                  <Textarea
                    id="suggestion"
                    value={suggestion}
                    onChange={(event) => setSuggestion(event.target.value)}
                    maxLength={1200}
                    rows={4}
                    className="rounded-[10px] border-border bg-white"
                  />
                </div>
                {feedbackMutation.error ? (
                  <p
                    className="mt-4 rounded-[10px] bg-[#fff0f2] px-3 py-2 text-xs text-[#8f2d3a]"
                    role="alert"
                  >
                    {feedbackMutation.error.message}
                  </p>
                ) : null}
                <Button
                  type="submit"
                  className="mt-5 w-full"
                  disabled={resetInProgress || feedbackMutation.isPending}
                >
                  {feedbackMutation.isPending
                    ? "Recording…"
                    : "Record demo feedback"}
                </Button>
              </form>
            )}
          </section>
        </aside>
      </div>
    </div>
  )
}
